import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchCoupons,
  deleteExistingCoupon,
} from "store/features/coupon/couponActions";
import {
  selectCoupons,
  selectCouponLoading,
} from "store/features/coupon/couponSelectors";
import {
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiPercent,
} from "react-icons/fi";
import { FaRupeeSign } from "react-icons/fa";
import { BsCheckCircleFill, BsXCircleFill } from "react-icons/bs";
import CreateCouponModal from "./CreateCouponModal";
import EditCouponModal from "./EditCouponModal";

const Coupon = () => {
  const dispatch = useDispatch();
  const coupons = useSelector(selectCoupons);
  const loading = useSelector(selectCouponLoading);

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedCoupon, setSelectedCoupon] = useState(null);

  useEffect(() => {
    dispatch(fetchCoupons());
  }, [dispatch]);

  const handleDelete = (id) => {
    if (window.confirm("Are you sure you want to delete this coupon?")) {
      dispatch(deleteExistingCoupon(id));
    }
  };

  const openEditModal = (coupon) => {
    setSelectedCoupon(coupon);
    setIsEditModalOpen(true);
  };

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-t-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 min-h-screen">
      <div className="mb-8 flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-800">Coupon Management</h1>
        <button
          onClick={() => setIsCreateModalOpen(true)}
          className="flex items-center rounded-lg bg-indigo-600 px-4 py-2 text-white transition-colors hover:bg-indigo-700"
        >
          <FiPlus className="mr-2" /> Create Coupon
        </button>
      </div>

      <div className="overflow-hidden rounded-lg bg-white shadow">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-indigo-600 text-white">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ">
                  Code
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ">
                  Discount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ">
                  Validity
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ">
                  Usage
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {coupons.length > 0 ? (
                coupons.map((coupon) => (
                  <tr key={coupon._id} className="hover:bg-gray-50">
                    <td className="whitespace-nowrap px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">
                        {coupon.code}
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4">
                      <div className="flex items-center">
                        {coupon.discountType === "percentage" ? (
                          <FiPercent className="mr-1 text-indigo-600" />
                        ) : (
                          
                          <FaRupeeSign className="mr-1 text-indigo-600" />
                        )}
                        <span className="text-sm text-gray-900">
                          {coupon.discountValue}
                          {coupon.discountType === "percentage" ? "" : ""}
                        </span>
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4">
                      <div className="text-sm text-gray-900">
                        {new Date(coupon.expirationDate).toLocaleDateString()}
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4">
                      <div className="text-sm text-gray-900">
                        {coupon.usedCount} / {coupon.usageLimit}
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4">
                      {coupon.isActive ? (
                        <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
                          <BsCheckCircleFill className="mr-1" /> Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-800">
                          <BsXCircleFill className="mr-1" /> Inactive
                        </span>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => openEditModal(coupon)}
                          className="text-indigo-600 hover:text-indigo-900"
                        >
                          <FiEdit2 className="text-2xl" />
                        </button>
                        <button
                          onClick={() => handleDelete(coupon._id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          <FiTrash2 className="text-2xl" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="6"
                    className="px-6 py-4 text-center text-sm text-gray-500"
                  >
                    No coupons found. Create one to get started.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create Coupon Modal */}
      <CreateCouponModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
      />

      {/* Edit Coupon Modal */}
      {selectedCoupon && (
        <EditCouponModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          coupon={selectedCoupon}
        />
      )}
    </div>
  );
};

export default Coupon;
